import { lazy, Suspense, useCallback } from 'react';
import type { ReactNode } from 'react';
import { Pin } from 'lucide-react';
import { api } from '@/core/lib/apiClient';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import { useSession } from '@/hooks/useSession';
import { networks } from './service';
import type { Capabilities, Family, Page } from './contracts';
import { ShortcutGroup, ShortcutLink } from './ShortcutLink';

const NetworkPage = lazy(() => import('./NetworkPages').then((m) => ({ default: m.NetworkPage })));
const Dashboard = lazy(() =>
  import('./NetworkPages').then((m) => ({ default: m.CreatorDashboardPage })),
);

/** Existing navigation destinations switch to real data only when enabled. */
export function NetworkEntry({
  family,
  fallback,
  destination,
}: {
  family: Family;
  fallback: ReactNode;
  destination: 'live' | 'creator';
}) {
  const { currentUser } = useSession();
  const result = useAsyncRead<Capabilities | null>(
    networks.capabilities,
    currentUser?.id ?? 'guest',
    null,
  );
  if (result.loading) return <p role="status">Loading…</p>;
  if (!result.data?.flags?.[family]) return fallback;
  return (
    <Suspense fallback={<p role="status">Loading…</p>}>
      {destination === 'creator' ? <Dashboard /> : <NetworkPage kind="events" />}
    </Suspense>
  );
}

export function PinnedMixes() {
  const { currentUser } = useSession();
  const load = useCallback(async () => {
    if (!currentUser) return null;
    const caps = await networks.capabilities();
    return caps.flags?.mixes_packs ? api<Page<'mixes'>>('/mixes/?pinned=true') : null;
  }, [currentUser]);
  const result = useAsyncRead<Page<'mixes'> | null>(load, currentUser?.id ?? 'guest', null);
  if (!result.data?.items.length) return null;
  return (
    <ShortcutGroup aria-label="Pinned Signal Mixes">
      {result.data.items.map((r) => (
        <ShortcutLink key={r.id} to={`/mixes/${encodeURIComponent(r.slug)}`}>
          <Pin size={14} aria-hidden="true" />
          {r.title}
        </ShortcutLink>
      ))}
    </ShortcutGroup>
  );
}
